import { useState } from 'react';
import { useSession } from '../hooks/useSession';

const inr = (n: number) => '₹' + Math.round(n).toLocaleString('en-IN');

/**
 * The four price-band ceilings buyers filter by. A listing priced at or under a ceiling
 * appears in that rail, which is why ₹31 can matter more than the margin it costs.
 */
const CEILINGS = [599, 899, 999, 1999];

/** Display-only conversion rates, matching the prototype. Buyers pay in local currency. */
const FX = { sgd: 0.0158, myr: 0.053 };

/**
 * Pricing and margin calculator.
 *
 * The commission field is prefilled from the vendor's own contracted rate, which we
 * already hold from login — the prototype had to tell vendors to "confirm it with your
 * account manager", which we can simply answer.
 */
export function CalculatorPage() {
  const { vendor } = useSession();

  const [price, setPrice] = useState('1020');
  const [cost, setCost] = useState('620');
  const [commission, setCommission] = useState(String(vendor?.commissionRate ?? 10));
  const [gateway, setGateway] = useState('2');

  const n = (v: string) => {
    const parsed = Number.parseFloat(v);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  };

  const p = n(price);
  const c = n(cost);
  const commPct = n(commission);
  const gatePct = n(gateway);

  const comm = (p * commPct) / 100;
  const gate = (p * gatePct) / 100;
  const payout = p - comm - gate;
  const profit = payout - c;
  const marginPct = p > 0 ? Math.round((profit / p) * 100) : 0;

  const under = CEILINGS.find((ceil) => p <= ceil);
  const nextCeil = [...CEILINGS].reverse().find((ceil) => ceil < p);

  let bandTitle: string;
  let bandAdvice: string;
  if (under) {
    bandTitle = `In the under ₹${under.toLocaleString('en-IN')} rail`;
    bandAdvice =
      `You appear in that budget rail and its filter. You have ` +
      `₹${Math.round(under - p).toLocaleString('en-IN')} of headroom before you drop out of ` +
      `it — useful if your costs move.`;
  } else if (nextCeil) {
    const drop = p - nextCeil + 1;
    const newProfit = (nextCeil - 1) * (1 - (commPct + gatePct) / 100) - c;
    bandTitle = `Above every rail, by ₹${Math.round(drop).toLocaleString('en-IN')}`;
    bandAdvice =
      `Dropping to ₹${(nextCeil - 1).toLocaleString('en-IN')} puts you in the under ` +
      `₹${nextCeil.toLocaleString('en-IN')} rail. Your profit per unit would go from ` +
      `${inr(profit)} to ${inr(newProfit)}. If the rail brings more than a handful of extra ` +
      `units, take it.`;
  } else {
    bandTitle = 'Premium, above all four bands';
    bandAdvice =
      'No rail will carry this. Growth here comes from search, category placement, reviews ' +
      'and creator features — see the growth track.';
  }

  const fields = [
    { id: 'price', label: 'Your listed price', prefix: '₹', suffix: '', value: price, set: setPrice, hint: 'What the buyer pays for the item, in rupees.' },
    { id: 'cost', label: 'Your cost per unit', prefix: '₹', suffix: '', value: cost, set: setCost, hint: 'Materials, labour, packaging — everything except platform fees.' },
    { id: 'commission', label: 'Platform commission', prefix: '', suffix: '%', value: commission, set: setCommission, hint: vendor ? 'Prefilled with your contracted rate.' : 'Your contracted rate.' },
    { id: 'gateway', label: 'Payment handling', prefix: '', suffix: '%', value: gateway, set: setGateway, hint: 'Gateway and FX handling on the order.' },
  ];

  const rows = [
    { k: 'Listed price', v: inr(p), color: '#fff', rule: false, bold: false },
    { k: 'Commission', v: '− ' + inr(comm), color: 'rgba(255,255,255,0.8)', rule: false, bold: false },
    { k: 'Payment handling', v: '− ' + inr(gate), color: 'rgba(255,255,255,0.8)', rule: false, bold: false },
    { k: 'Your payout', v: inr(payout), color: 'var(--amber-300)', rule: true, bold: true },
    { k: 'Your cost', v: '− ' + inr(c), color: 'rgba(255,255,255,0.8)', rule: false, bold: false },
    { k: 'Profit per unit', v: `${inr(profit)} (${marginPct}%)`, color: profit > 0 ? '#7BE0A0' : '#FF9A8A', rule: true, bold: true },
  ];

  return (
    <div className="vu-wrap">
      <div className="vu-kicker">Tool</div>
      <h1 className="vu-h1">Pricing and margin calculator</h1>
      <p className="vu-lede" style={{ maxWidth: '62ch' }}>
        Work out what you actually keep per unit, what your buyer sees in Singapore dollars
        and ringgit, and whether dropping under the next price band is worth it.
      </p>

      <div
        className="vu-grid"
        style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', marginTop: 'var(--space-6)', alignItems: 'start' }}
      >
        <div className="vu-card" style={{ padding: 'var(--space-6)' }}>
          {fields.map((f) => (
            <label key={f.id} style={{ display: 'block', marginBottom: 'var(--space-5)' }}>
              <span style={{ display: 'block', fontWeight: 'var(--fw-bold)', marginBottom: 6, color: 'var(--text-strong)', fontSize: 'var(--fs-sm)' }}>
                {f.label}
              </span>
              <span
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)',
                  padding: '0 14px', background: 'var(--surface-page)',
                }}
              >
                {f.prefix && <span className="vu-muted">{f.prefix}</span>}
                <input
                  type="number"
                  min="0"
                  step="any"
                  inputMode="decimal"
                  value={f.value}
                  onChange={(e) => f.set(e.target.value)}
                  style={{
                    flex: 1, border: 0, background: 'none', outline: 'none',
                    padding: '13px 0', fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)',
                    color: 'var(--text-strong)', minWidth: 0, font: 'inherit',
                  }}
                />
                {f.suffix && <span className="vu-muted">{f.suffix}</span>}
              </span>
              <span className="vu-subtle" style={{ display: 'block', marginTop: 6, fontSize: 'var(--fs-xs)' }}>
                {f.hint}
              </span>
            </label>
          ))}
        </div>

        <div>
          <div className="vu-card" style={{ background: 'var(--vu-ink)', borderColor: 'transparent', padding: 'var(--space-6)' }}>
            <div
              style={{
                fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', letterSpacing: '.14em',
                color: 'var(--brand-accent)', marginBottom: 'var(--space-4)',
              }}
            >
              PER UNIT
            </div>
            {rows.map((r) => (
              <div
                key={r.k}
                style={{
                  display: 'flex', justifyContent: 'space-between', gap: 'var(--space-4)',
                  padding: 'var(--space-3) 0',
                  borderTop: r.rule ? '1px solid rgba(255,255,255,0.18)' : undefined,
                  fontWeight: r.bold ? 'var(--fw-bold)' : 'var(--fw-regular)',
                }}
              >
                <span style={{ color: 'rgba(255,255,255,0.72)' }}>{r.k}</span>
                <span style={{ color: r.color, whiteSpace: 'nowrap' }}>{r.v}</span>
              </div>
            ))}
            <p style={{ margin: 'var(--space-4) 0 0', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6 }}>
              Anything under about 25% net margin leaves you no room to discount, run a
              campaign or absorb a return.
            </p>
          </div>

          <div
            className="vu-card"
            style={{ marginTop: 'var(--space-4)', background: 'var(--orange-50)', borderColor: 'var(--orange-100)' }}
          >
            <div className="vu-kicker">Band position</div>
            <div className="vu-h3" style={{ fontSize: 'var(--fs-lg)' }}>{bandTitle}</div>
            <p className="vu-muted" style={{ margin: 'var(--space-3) 0 0', fontSize: 'var(--fs-sm)', lineHeight: 1.6 }}>
              {bandAdvice}
            </p>
          </div>

          <div className="vu-card" style={{ marginTop: 'var(--space-4)' }}>
            <h2 className="vu-h3" style={{ fontSize: 'var(--fs-lg)', marginBottom: 'var(--space-2)' }}>Buyer sees</h2>
            {[
              { k: 'Singapore', v: 'S$' + (p * FX.sgd).toFixed(2) },
              { k: 'Malaysia', v: 'RM' + (p * FX.myr).toFixed(2) },
            ].map((row) => (
              <div
                key={row.k}
                style={{
                  display: 'flex', justifyContent: 'space-between',
                  padding: 'var(--space-3) 0', borderBottom: '1px solid var(--border-subtle)',
                  fontSize: 'var(--fs-sm)',
                }}
              >
                <span>{row.k}</span>
                <span style={{ color: 'var(--teal-400)', fontWeight: 'var(--fw-bold)' }}>{row.v}</span>
              </div>
            ))}
            <p className="vu-subtle" style={{ margin: 'var(--space-3) 0 0', fontSize: 'var(--fs-xs)', lineHeight: 1.6 }}>
              Shipping, the S$15 express surcharge and any parcel fee sit on top and are
              charged by us, not by you.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
